/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { UIEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { spacing } from "../../../../../global/emotion";
import { Text } from "../../../../components/_base/_core/Text/Text.emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type {
  TranscriptCallbackProps,
  TranscriptContentProps,
  TranscriptPlaybackProps,
  TranscriptSettingsProps,
} from "../../../../types/transcriptV2.types";
import { useAutoScroll } from "../../../hooks/useAutoScroll";
import { useTranscript } from "../../../hooks/useTranscript";
import { TranscriptContainer } from "./TranscriptContainer.emotion";
import { TranscriptContent } from "./TranscriptContent.emotion";
import { TranscriptFooter } from "./TranscriptFooter.emotion";
import { TranscriptHeader } from "./TranscriptHeader.emotion";
import { TranscriptDisplayProvider } from "./TranscriptSettingsContext";
import { TranscriptSkeleton } from "./TranscriptSkeleton.emotion";

const HEADER_COLLAPSED_SCROLL_THRESHOLD = 75;

const loadingErrorContainerStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  color: theme.semanticColors.primary,
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  paddingTop: spacing.xl,
  placeContent: "flex-start",
  placeItems: "center",
});
const bodyStyle: Interpolation<ThemeType> = (theme) => ({
  fontFamily: theme.fontFamilies.serif,
  marginTop: "210px",
  maxWidth: "640px",
  padding: `${spacing.l} ${spacing.l} ${spacing.mediaPlayerMobile}`,
  [cssMediaQueryMinWidthTablet]: {
    padding: `${spacing.l} ${spacing.l} ${spacing.mediaPlayerDesktop}`,
    marginTop: "280px",
  },
  transition: "font-size 250ms ease-out, line-height 250ms ease-out",
  whiteSpace: "pre-line",
});
const bodyFontOverrideStyle: Interpolation<number> = (fontMultiplier) => ({
  fontSize: `${fontMultiplier * 20}px`,
  lineHeight: `${fontMultiplier * 40}px`,
});

type TranscriptProps = {
  content: TranscriptContentProps;
  playback: TranscriptPlaybackProps;
  settings: TranscriptSettingsProps;
  callbacks: TranscriptCallbackProps;
  children: React.ReactNode;
  isLoading?: boolean;
};

export const Transcript = ({
  content,
  playback,
  settings,
  callbacks,
  children,
  isLoading = false,
}: TranscriptProps) => {
  const hasBeenViewed = useRef(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState<boolean>(false);
  const t = useTranslations();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { transcriptUrl, transcriptType, isAutotranscribed } = content;

  const { playing, currentTime = 0, isAutoScrolling = true } = playback;

  const {
    onPlay,
    onError,
    onReady,
    onReport,
    onUserScroll,
    onFirstViewed,
    excludeFromUserInteractionRef,
  } = callbacks;

  const {
    data: transcriptData,
    isLoading: isTranscriptLoading,
    error: transcriptError,
  } = useTranscript(transcriptUrl, transcriptType, {
    onError,
  });

  useEffect(() => {
    if (!hasBeenViewed.current) {
      hasBeenViewed.current = true;
      onFirstViewed?.();
    }
  }, [onFirstViewed]);

  useEffect(() => {
    if (!isTranscriptLoading && !transcriptError && transcriptData) {
      onReady?.();
    }
  }, [isTranscriptLoading, transcriptError, transcriptData, onReady]);

  const memoizedSegments = useMemo(
    () => transcriptData?.segments || [],
    [transcriptData?.segments],
  );

  const { handleUserScroll } = useAutoScroll({
    currentTime,
    segments: memoizedSegments,
    isAutoScrolling,
    isAutoScrollEnabled: settings.isAutoScrollEnabled,
    options: {
      onUserScroll,
      refreshTrigger: {
        value: `${settings.fontMultiplier}-${settings.isAutoScrollEnabled}`,
        timeout: 300,
      },
      excludeFromUserInteractionRef,
      scrollContainerRef,
    },
  });

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const scrollTop = (e.target as HTMLDivElement).scrollTop ?? 0;
      setIsHeaderCollapsed(scrollTop > HEADER_COLLAPSED_SCROLL_THRESHOLD);
      handleUserScroll();
    },
    [setIsHeaderCollapsed, handleUserScroll],
  );

  if (isLoading || isTranscriptLoading) {
    return <TranscriptSkeleton />;
  }

  if (transcriptError) {
    return (
      <div css={loadingErrorContainerStyle}>
        <Text>{t("failed_to_load_transcript")}</Text>
      </div>
    );
  }

  return (
    <TranscriptDisplayProvider
      theme={settings.theme}
      colorHex={settings.colorHex}
      fontMultiplier={settings.fontMultiplier}
      showAllBlocksAsSeen={
        !(settings.isAutoScrollEnabled && content.isCurrentItem)
      }
    >
      <TranscriptContainer
        isAutoScrolling={isAutoScrolling}
        isHeaderCollapsed={isHeaderCollapsed}
        onScroll={handleScroll}
        scrollContainerRef={scrollContainerRef}
      >
        <TranscriptHeader
          content={content}
          isHeaderCollapsed={isHeaderCollapsed}
          playing={playing}
          onPlay={onPlay}
        />
        {children}
        <article
          css={[bodyStyle, bodyFontOverrideStyle(settings.fontMultiplier)]}
        >
          <TranscriptContent
            transcriptData={transcriptData}
            transcriptType={transcriptType}
            currentTime={currentTime}
          />
          {transcriptData?.textContent && (
            <TranscriptFooter
              onReport={onReport}
              isAutotranscribed={isAutotranscribed}
            />
          )}
        </article>
      </TranscriptContainer>
    </TranscriptDisplayProvider>
  );
};
